import _ from 'lodash';
import React from 'react';

import {
  Close,
  Done,
  Refresh
} from '@mui/icons-material';
import {
  Box,
  Button,
  Grid,
  keyframes,
  Link,
  Paper,
  Skeleton,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import {
  fields,
  Form
} from 'iron-forge-react-modules';
import {
  useForm,
  useWatch
} from 'react-hook-form';

import { selectFields } from 'modules';
import { useSearchParams } from 'react-router-dom';
import * as dataService from './dataService';
import * as types from './types';



const { genController } = selectFields.StoreSelectField.genComponentAndControllerGenerator();
const ControlledStoreSelectField = genController<types.IFilter>();



const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;
const ControlledTextField = fields.TextField.genControllerComponent<types.IFilter>();

const HeaderBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  padding: theme.spacing(2)
}));



export const FC: React.FC = () => {
  const [search, setSearch] = useSearchParams();
  const searchText = search.get('search') ?? '';

  const form = useForm<types.IFilter>({
    defaultValues: {
      omniFreeText: searchText
    }
  });
  const watch = useWatch({ control: form.control });
  const [refetching, setRefetching] = React.useState(false);
  const queryTuple = dataService.useQuery({
    omniFreeText: watch.omniFreeText ?? '',
    store: watch.store ? {
      ...watch.store,
      id: watch.store.id ?? '',
      displayName: watch.store.displayName ?? ''
    } : null
  });


  const loadingRows = React.useMemo(
    () => _.times(10).map(i => (
      <TableRow key={i}>
        <TableCell>
          <Skeleton />
        </TableCell>

        <TableCell>
          <Skeleton />
        </TableCell>

        <TableCell>
          <Skeleton />
        </TableCell>

        <TableCell>
          <Skeleton />
        </TableCell>

        <TableCell>
          <Skeleton />
        </TableCell>

        <TableCell>
          <Skeleton />
        </TableCell>

        <TableCell>
          <Skeleton />
        </TableCell>
      </TableRow>
    )),
    []);

  const processSubmit = React.useCallback(
    (formState: types.IFilter) => {
      setSearch(params => {
        params.set('search', formState.omniFreeText);
        return params;
      });
    },
    [setSearch]
  );

  return (
    <Paper>
      <HeaderBox>
        <Form.FC
          disableDefaultOverflow
          onSubmit={form.handleSubmit(processSubmit)}>
          <Grid
            container
            spacing={1}>
            <Grid
              item
              xs={3}>
              <ControlledTextField
                fieldKey="omniFreeText"
                label="Search"
                formControl={form.control} />
            </Grid>

            <Grid
              item
              xs={3}>
              <ControlledStoreSelectField
                label="Store"
                isDisabled={queryTuple.loading}
                fieldKey="store"
                formControl={form.control} />
            </Grid>

            <Grid
              item
              xs={5}
              md={2}>
              <Button type="submit">
                Search
              </Button>
            </Grid>
          </Grid>
        </Form.FC>

        <Box
          display="flex"
          alignItems="flex-start"
          gap={2}>
          <Button
            startIcon={<Refresh sx={{ animation: refetching ? `${spin} 2s infinite linear` : undefined }} />}
            onClick={async () => {
              setRefetching(true);
              await queryTuple.refetch();
              setRefetching(false);
            }}>
            Refresh
          </Button>

          <Typography pt={'6px'}>
            <b>{queryTuple.data.pendingOrders.length}</b> pending writes
          </Typography>
        </Box>
      </HeaderBox>


      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Order Number</TableCell>
              <TableCell>Order Date</TableCell>
              <TableCell>Customer Written</TableCell>
              <TableCell>Shipping Service</TableCell>
              <TableCell>Payment Method</TableCell>
              <TableCell>SKUS In NetSuite</TableCell>
              <TableCell>Discount Items Synced</TableCell>
              <TableCell>Write Request Generated</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {(queryTuple.loading) ? loadingRows : queryTuple.data.pendingOrders.map(order => (
              <TableRow key={order.id}>
                <TableCell><Link target="_blank" href={order.urlToOrder}>{order.orderNumber}</Link></TableCell>
                <TableCell>{order.orderDate.toFormat('yyyy-MM-dd')}</TableCell>

                {[
                  order.customerInNetSuite,
                  order.hasShippingService,
                  order.hasMappedPaymentMethod,
                  order.allProductsInNetSuite,
                  order.discountItemsSynced,
                  order.writeRequestGenerated
                ].map((val, i) => (
                  <TableCell key={i}>
                    {val ? (
                      <Done color="success" />
                    ) : (
                      <Close color="warning" />
                    )}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

